/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {
    NoteFinderPageOverlayNote_Note$data
} from "ui/feature/root/note-select/__generated__/NoteFinderPageOverlayNote_Note.graphql";
import styles from "./NoteFinderPageOverlayNote.module.scss";
import ReactTooltip from "react-tooltip";
import NoteFinderPageOverlayNoteBulb from "ui/feature/root/note-select/NoteFinderPageOverlayNoteBulb";

interface Props {
    note: NoteFinderPageOverlayNote_Note$data;
    pageWidth: number;
    pageHeight: number;
}

const NoteFinderPageOverlayNote: React.FC<Props> = ({note, pageHeight, pageWidth}) => {

    const width = React.useMemo(() => {
        return `${(note.width / pageWidth) * 100}%`;
    }, [note.width, pageWidth])

    const height = React.useMemo(() => {
        return `${(note.height / pageHeight) * 100}%`;
    }, [note.height, pageHeight]);

    const left = React.useMemo(() => {
        return `${(note.x / pageWidth) * 100}%`;
    }, [note.x, pageWidth]);

    const top = React.useMemo(() => {
        return `${(note.y / pageHeight) * 100}%`;
    }, [note.y, pageHeight]);

    const onNoteClick = React.useCallback(() => {
        console.group(`note ${note.note_id}`)
        console.log(`page_height=${pageHeight},page_width=${pageWidth}`);
        console.log(`types=${note.note_types_in_order_of_likelihood_displayable.map((x) => x.note_type).join(", ")}`);
        console.log(`x=${note.x},y=${note.y},w=${note.width},h=${note.height}`);
        console.log(`finder_confidence=${note.finder_confidence}`)
        console.log(`has_dot=${note.has_dot}`);
        if (note.bar_information) {
            console.log(`bar_information: total_number_of_vertical_lines=${note.bar_information.total_number_of_vertical_lines}`)
        }
        console.group("bulbs");
        for (const bulb of note.bulbs) {
            console.group(`bulb ${bulb.bulb_id}`);
            console.log(`x=${bulb.x + note.x}, y=${bulb.y + note.y}, w=${bulb.width}, h=${bulb.height}`);
            console.log(`back_accessories=${bulb.back_accessory_types_in_order_or_likelihood}`);
            console.log(`median_spacing=${bulb.median_spacing}`);
            console.log(`line_placement=${bulb.line_placement}`);
            if (bulb.bar_information) {
                console.log(`bar_information: bar_note_line_idx=${bulb.bar_information.bar_note_line_idx}, bar_note_number_of_top_lines=${bulb.bar_information.bar_note_number_of_top_lines}`)
            }
            console.group(`main_lines (${bulb.main_lines.length})`);
            for (const line of bulb.main_lines) {
                console.log(`a=${line.a}, b=${line.b}`);
            }
            console.groupEnd();
            console.groupEnd();
        }
        console.groupEnd()
        console.groupEnd()
    }, [note.bar_information, note.bulbs, note.finder_confidence, note.has_dot, note.height, note.note_id, note.note_types_in_order_of_likelihood_displayable, note.width, note.x, note.y, pageHeight, pageWidth]);

    const tooltipName = `${note.note_id}-tooltip`

    return (<>
        <ReactTooltip place={"top"} type={"light"} effect={"solid"} id={tooltipName}>
            <p className={styles.tooltipContent}><b>{note.note_types_in_order_of_likelihood_displayable[0].title}</b>
            </p>
            <p className={styles.tooltipContent}>Confidence in region: {(note.finder_confidence * 100).toFixed(1)}%</p>
        </ReactTooltip>
        <div
            onClick={onNoteClick}
            data-tip
            data-for={tooltipName}
            className={styles.note}
            style={{
                width: width,
                height: height,
                left: left,
                top: top
            }}>
            {(note.note_types_in_order_of_likelihood_displayable[0].is_audible ? note.bulbs : []).map((bulb) =>
                <NoteFinderPageOverlayNoteBulb key={bulb.bulb_id} bulb={bulb} noteHeight={note.height}
                                               noteWidth={note.width} noteX={note.x} noteY={note.y}/>)}
        </div>
    </>);
}

export default createFragmentContainer(
    NoteFinderPageOverlayNote,
    {
        note: graphql`
        fragment NoteFinderPageOverlayNote_Note on MusicParseStepDataNoteDataSourceCombineNote {
            note_id
            x
            y
            height
            width
            finder_confidence
            has_dot
            note_types_in_order_of_likelihood_displayable {
                note_type
                title
                is_audible
            }
            bar_information {
                total_number_of_vertical_lines
            }
            bulbs {
                bulb_id
                x
                y
                width
                height
                median_spacing: median_spacing_between_lines
                line_placement
                bar_information {
                    bar_note_line_idx
                    bar_note_number_of_top_lines
                }
                main_lines {
                    a
                    b
                }
                back_accessory_types_in_order_or_likelihood
                ...NoteFinderPageOverlayNoteBulb_Bulb
            }
        }
        `
    }
);